var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Typography, Button, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Alert, Snackbar, Dialog, DialogTitle, DialogContent, TextField, FormControlLabel, Switch, CircularProgress, Divider, MenuItem } from '@mui/material';
import { NavigateBefore, NavigateNext, Save as SaveIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { format, addDays } from 'date-fns';
import { es } from 'date-fns/locale';
// Import our new services and components
import { inventoryPlanningService, formatRestrictionValue, getBooleanRestrictionTypes, getNumericRestrictionTypes } from '@/services/inventoryPlanningService';
import RestrictionFilter from '@/components/inventory/RestrictionFilter';
import RoomTypeFilter from '@/components/inventory/RoomTypeFilter';
import RatePlanFilter from '@/components/inventory/RatePlanFilter';
// Styled components
var StyledTableContainer = styled(TableContainer)(function (_a) {
    var theme = _a.theme;
    return ({
        maxHeight: 'calc(100vh - 300px)',
        overflow: 'auto',
        '& .MuiTableCell-root': {
            padding: '4px 8px',
            fontSize: '0.75rem',
            minWidth: '60px',
            border: '1px solid #e0e0e0',
        },
        '& .MuiTableCell-head': {
            backgroundColor: theme.palette.grey[100],
            fontWeight: 'bold',
            position: 'sticky',
            top: 0,
            zIndex: 10,
        },
    });
});
var StyledTableCell = styled(TableCell, {
    shouldForwardProp: function (prop) { return !['editable', 'past', 'cellType', 'hasPendingChange', 'isRangeSelected', 'isRangeStart', 'cellValue', 'hasStopSell'].includes(prop); },
})(function (_a) {
    var theme = _a.theme, editable = _a.editable, past = _a.past, cellType = _a.cellType, hasPendingChange = _a.hasPendingChange, isRangeSelected = _a.isRangeSelected, isRangeStart = _a.isRangeStart, cellValue = _a.cellValue, hasStopSell = _a.hasStopSell;
    // Determine if this cell should have red background
    var shouldBeRed = (cellType === 'availability' && cellValue === 0) || hasStopSell;
    return {
        cursor: editable && !past ? 'pointer' : 'default',
        textAlign: 'center', // Center all values
        verticalAlign: 'middle',
        backgroundColor: past
            ? theme.palette.grey[100]
            : shouldBeRed
                ? '#ffebee' // Light red for zero availability or stop sell
                : hasPendingChange
                    ? '#fff3cd' // Light yellow for pending changes
                    : isRangeSelected
                        ? '#e3f2fd' // Light blue for range selection
                        : isRangeStart
                            ? '#1976d2' // Blue for range start
                            : editable
                                ? theme.palette.common.white
                                : theme.palette.grey[50],
        border: hasPendingChange
            ? "2px solid ".concat(theme.palette.warning.main)
            : shouldBeRed
                ? "1px solid #f44336" // Red border for zero availability or stop sell
                : isRangeSelected || isRangeStart
                    ? "2px solid ".concat(theme.palette.info.main)
                    : '1px solid #e0e0e0',
        '&:hover': {
            backgroundColor: editable && !past ? theme.palette.action.hover : undefined,
        },
        color: shouldBeRed
            ? '#d32f2f' // Dark red text for zero availability or stop sell
            : cellType === 'restriction'
                ? theme.palette.mode === 'dark' ? theme.palette.common.white : '#333333' // Ensure restriction text is always visible
                : isRangeStart
                    ? theme.palette.common.white
                    : theme.palette.text.primary,
        fontWeight: cellType === 'rate' || hasPendingChange || shouldBeRed ? 'bold' : 'normal',
        position: 'relative',
        padding: '8px',
        minWidth: '60px',
    };
});
// Header cells with hierarchy levels
var RoomTypeHeaderCell = styled(TableCell)(function (_a) {
    var theme = _a.theme;
    return ({
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        fontWeight: 'bold',
        fontSize: '0.8rem',
        textAlign: 'left',
        minWidth: '120px',
        position: 'sticky',
        left: 0,
        zIndex: 20,
        borderBottom: "2px solid ".concat(theme.palette.primary.dark),
    });
});
var RatePlanHeaderCell = styled(TableCell)(function (_a) {
    var theme = _a.theme;
    return ({
        backgroundColor: theme.palette.secondary.main,
        color: theme.palette.secondary.contrastText,
        fontWeight: 'bold',
        fontSize: '0.75rem',
        textAlign: 'left',
        minWidth: '120px',
        paddingLeft: '20px', // Indentation to show hierarchy
        position: 'sticky',
        left: 0,
        zIndex: 20,
    });
});
var RestrictionHeaderCell = styled(TableCell)(function (_a) {
    var theme = _a.theme;
    return ({
        backgroundColor: theme.palette.grey[600],
        color: theme.palette.common.white,
        fontWeight: 'normal',
        fontSize: '0.7rem',
        textAlign: 'left',
        minWidth: '120px',
        paddingLeft: '40px', // More indentation for restrictions
        position: 'sticky',
        left: 0,
        zIndex: 20,
    });
});
var InventoryCalendarV2 = function (_a) {
    var _b, _c;
    var propertyId = _a.propertyId;
    // State management
    var _d = useState(null), planningData = _d[0], setPlanningData = _d[1];
    var _e = useState(true), loading = _e[0], setLoading = _e[1];
    var _f = useState(false), saving = _f[0], setSaving = _f[1];
    var _g = useState(null), error = _g[0], setError = _g[1];
    var _h = useState(null), success = _h[0], setSuccess = _h[1];
    var _j = useState({ open: false }), editDialog = _j[0], setEditDialog = _j[1];
    var _k = useState(0), pendingChangesCount = _k[0], setPendingChangesCount = _k[1];
    // Range selection state
    var _l = useState(false), rangeSelectionMode = _l[0], setRangeSelectionMode = _l[1];
    var _m = useState({}), selectedRange = _m[0], setSelectedRange = _m[1];
    // Filter states
    var _o = useState({
        restrictions: ['RATE_AND_AVAILABILITY'], // Default: show availability + rates
        roomTypes: [],
        ratePlans: [],
        dateRange: {
            from: new Date().toISOString().split('T')[0],
            to: new Date(Date.now() + 13 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        }
    }), filters = _o[0], setFilters = _o[1];
    // Date navigation
    var _p = useState(new Date()), currentWeekStart = _p[0], setCurrentWeekStart = _p[1];
    // Initialize planning data
    useEffect(function () {
        var initializeData = function () { return __awaiter(void 0, void 0, void 0, function () {
            var data, err_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 3, 4, 5]);
                        setLoading(true);
                        setError(null);
                        // Load interface data first
                        return [4 /*yield*/, inventoryPlanningService.loadInterfaceData(propertyId)];
                    case 1:
                        // Load interface data first
                        _a.sent();
                        // Update service filters before loading calendar data
                        inventoryPlanningService.setFilters(filters);
                        // Then load calendar data
                        return [4 /*yield*/, inventoryPlanningService.loadCalendarData(propertyId)];
                    case 2:
                        // Then load calendar data
                        _a.sent();
                        data = inventoryPlanningService.getFilteredData();
                        console.log('Loaded planning data:', data);
                        setPlanningData(data);
                        return [3 /*break*/, 5];
                    case 3:
                        err_1 = _a.sent();
                        console.error('Error initializing planning data:', err_1);
                        setError('Error loading inventory data');
                        return [3 /*break*/, 5];
                    case 4:
                        setLoading(false);
                        return [7 /*endfinally*/];
                    case 5: return [2 /*return*/];
                }
            });
        }); };
        if (propertyId) {
            initializeData();
        }
        // Set up callback for calendar updates
        inventoryPlanningService.setCalendarUpdateCallback(function () {
            var data = inventoryPlanningService.getFilteredData();
            if (data) {
                setPlanningData(data);
            }
        });
    }, [propertyId, filters.dateRange]);
    // Generate date columns for the current view
    var dateColumns = useMemo(function () {
        var dates = [];
        var start = new Date(currentWeekStart);
        for (var i = 0; i < 14; i++) {
            var date = addDays(start, i);
            dates.push(date.toISOString().split('T')[0]);
        }
        return dates;
    }, [currentWeekStart]);
    // Get filtered data
    var filteredData = useMemo(function () {
        console.log('filteredData useMemo - planningData:', planningData);
        if (!planningData)
            return null;
        inventoryPlanningService.setFilters(filters);
        var result = inventoryPlanningService.getFilteredData();
        console.log('filteredData useMemo - result:', result);
        return result;
    }, [planningData, filters]);
    // Get visible restrictions based on filter
    var visibleRestrictions = useMemo(function () {
        return inventoryPlanningService.getVisibleRestrictions();
    }, [filters.restrictions]);
    // Handle filter changes
    var handleFilterChange = useCallback(function (newFilters) {
        setFilters(function (prev) { return (__assign(__assign({}, prev), newFilters)); });
    }, []);
    // Handle date navigation
    var navigateWeek = useCallback(function (direction) {
        setCurrentWeekStart(function (prev) {
            var newDate = new Date(prev);
            newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
            return newDate;
        });
    }, []);
    // Handle cell click for editing
    var handleCellClick = useCallback(function (cellData) {
        if (!cellData.editable)
            return;
        // Handle range selection mode
        if (rangeSelectionMode) {
            if (!selectedRange.start) {
                // Start range selection
                setSelectedRange({
                    start: cellData.date,
                    cellData: cellData
                });
            }
            else if (!selectedRange.end) {
                // Complete range selection and open dialog
                var startDate = selectedRange.start;
                var endDate = cellData.date;
                // Ensure dates are in correct order
                var from = startDate <= endDate ? startDate : endDate;
                var to = startDate <= endDate ? endDate : startDate;
                setEditDialog({
                    open: true,
                    cellData: selectedRange.cellData || cellData,
                    initialValue: cellData.value,
                    dateRange: { from: from, to: to }
                });
                // Reset range selection
                setSelectedRange({});
                setRangeSelectionMode(false);
            }
            else {
                // Reset and start new range
                setSelectedRange({
                    start: cellData.date,
                    cellData: cellData
                });
            }
        }
        else {
            // Normal single cell edit
            setEditDialog({
                open: true,
                cellData: cellData,
                initialValue: cellData.value,
                dateRange: {
                    from: cellData.date,
                    to: cellData.date
                }
            });
        }
    }, [rangeSelectionMode, selectedRange]);
    // Handle cell edit (add to pending changes)
    var handleSaveCell = useCallback(function (newValue, dateRange, currency) { return __awaiter(void 0, void 0, void 0, function () {
        var effectiveDateRange, startDate, endDate, changeCount, d, dateStr, rangeCellData, changeValue, changeValue;
        return __generator(this, function (_a) {
            if (!editDialog.cellData)
                return [2 /*return*/];
            try {
                effectiveDateRange = dateRange || editDialog.dateRange;
                if (effectiveDateRange && effectiveDateRange.from !== effectiveDateRange.to) {
                    startDate = new Date(effectiveDateRange.from);
                    endDate = new Date(effectiveDateRange.to);
                    changeCount = 0;
                    for (d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
                        dateStr = d.toISOString().split('T')[0];
                        rangeCellData = __assign(__assign({}, editDialog.cellData), { date: dateStr });
                        changeValue = editDialog.cellData.type === 'rate' && currency
                            ? { value: newValue, currency: currency }
                            : newValue;
                        inventoryPlanningService.addPendingChange(rangeCellData, changeValue);
                        changeCount++;
                    }
                    setPendingChangesCount(inventoryPlanningService.getPendingChangesCount());
                    setSuccess("Changes added for ".concat(changeCount, " dates. Click \"Save Changes\" to apply all changes."));
                }
                else {
                    changeValue = editDialog.cellData.type === 'rate' && currency
                        ? { value: newValue, currency: currency }
                        : newValue;
                    inventoryPlanningService.addPendingChange(editDialog.cellData, changeValue);
                    setPendingChangesCount(inventoryPlanningService.getPendingChangesCount());
                    setSuccess('Change added to batch. Click "Save Changes" to apply all changes.');
                }
                setEditDialog({ open: false });
            }
            catch (err) {
                console.error('Error adding change to batch:', err);
                setError('Error adding change to batch');
                setEditDialog({ open: false });
            }
            return [2 /*return*/];
        });
    }); }, [editDialog.cellData, editDialog.dateRange]);
    // Handle batch save
    var handleSaveBatch = useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        var results, currentData, err_2;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (pendingChangesCount === 0)
                        return [2 /*return*/];
                    setSaving(true);
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, 4, 5]);
                    return [4 /*yield*/, inventoryPlanningService.savePendingChanges()];
                case 2:
                    results = _a.sent();
                    console.log(results);
                    if (results.success > 0) {
                        setSuccess("Successfully saved ".concat(results.success, " changes").concat(results.failed > 0 ? ", ".concat(results.failed, " failed") : ''));
                        currentData = inventoryPlanningService.getFilteredData();
                        if (currentData) {
                            setPlanningData(currentData);
                        }
                        setPendingChangesCount(0);
                    }
                    else {
                        setError('Failed to save changes');
                    }
                    return [3 /*break*/, 5];
                case 3:
                    err_2 = _a.sent();
                    console.error('Error saving batch:', err_2);
                    setError('Error saving changes');
                    return [3 /*break*/, 5];
                case 4:
                    setSaving(false);
                    return [7 /*endfinally*/];
                case 5: return [2 /*return*/];
            }
        });
    }); }, [pendingChangesCount, propertyId]);
    // Handle cancel changes
    var handleCancelChanges = useCallback(function () {
        inventoryPlanningService.clearPendingChanges();
        setPendingChangesCount(0);
        setSuccess('All pending changes have been cancelled.');
    }, []);
    // Render cell content
    var renderCellContent = useCallback(function (roomTypeId, ratePlanId, date, restrictionType) {
        var cellData = inventoryPlanningService.getCellData(roomTypeId, ratePlanId, date, restrictionType);
        if (!cellData)
            return '—';
        var formattedValue = restrictionType
            ? formatRestrictionValue(restrictionType, cellData.value)
            : cellData.value;
        var isPast = new Date(date) < new Date();
        var cellKey = "".concat(roomTypeId || '', "-").concat(ratePlanId || '', "-").concat(date, "-").concat(restrictionType || '');
        var hasPendingChange = inventoryPlanningService.hasPendingChange(roomTypeId, ratePlanId, date, restrictionType);
        // Check for stop sell restriction
        var hasStopSell = restrictionType === 'stop_sell' && cellData.value === true;
        // Range selection visual feedback
        var isRangeStart = selectedRange.start === date;
        var isInRange = selectedRange.start && selectedRange.end &&
            date >= (selectedRange.start <= selectedRange.end ? selectedRange.start : selectedRange.end) &&
            date <= (selectedRange.start <= selectedRange.end ? selectedRange.end : selectedRange.start);
        return (_jsx(StyledTableCell, { editable: cellData.editable, past: isPast, cellType: cellData.type, hasPendingChange: hasPendingChange, isRangeSelected: Boolean(isInRange), isRangeStart: isRangeStart, cellValue: cellData.value, hasStopSell: hasStopSell, onClick: function () { return handleCellClick(cellData); }, children: _jsxs(Box, { position: "relative", children: [formattedValue, hasPendingChange && (_jsx(Box, { component: "span", sx: {
                            position: 'absolute',
                            top: -2,
                            right: -2,
                            fontSize: '8px',
                            color: 'orange',
                            fontWeight: 'bold'
                        }, children: "\u25CF" }))] }) }, cellKey));
    }, [handleCellClick]);
    // Loading state
    if (loading) {
        return (_jsxs(Box, { display: "flex", justifyContent: "center", alignItems: "center", height: "400px", children: [_jsx(CircularProgress, {}), _jsx(Typography, { variant: "h6", sx: { ml: 2 }, children: "Loading inventory data..." })] }));
    }
    // Error state
    if (error && !filteredData) {
        return (_jsx(Alert, { severity: "error", sx: { mt: 2 }, children: error }));
    }
    if (!filteredData) {
        return (_jsx(Alert, { severity: "info", sx: { mt: 2 }, children: "No data available for the selected property." }));
    }
    return (_jsxs(Box, { sx: { height: '100%', display: 'flex', flexDirection: 'column' }, children: [_jsx(Paper, { sx: { p: 2, mb: 2 }, children: _jsxs(Grid, { container: true, spacing: 3, alignItems: "flex-start", children: [_jsx(Grid, { item: true, xs: 12, md: 3, children: _jsx(RestrictionFilter, { selectedRestrictions: filters.restrictions, onRestrictionChange: function (restrictions) {
                                    return handleFilterChange({ restrictions: restrictions });
                                } }) }), _jsx(Grid, { item: true, xs: 12, md: 3, children: _jsx(RoomTypeFilter, { roomTypes: filteredData.interfaceData.roomTypes, selectedRoomTypes: filters.roomTypes, onRoomTypeChange: function (roomTypes) {
                                    return handleFilterChange({ roomTypes: roomTypes });
                                } }) }), _jsx(Grid, { item: true, xs: 12, md: 3, children: _jsx(RatePlanFilter, { ratePlans: filteredData.interfaceData.ratePlans, selectedRatePlans: filters.ratePlans, onRatePlanChange: function (ratePlans) {
                                    return handleFilterChange({ ratePlans: ratePlans });
                                } }) }), _jsx(Grid, { item: true, xs: 12, md: 3, children: _jsxs(Box, { display: "flex", gap: 1, alignItems: "center", height: "100%", children: [_jsx(Button, { variant: "outlined", startIcon: _jsx(RefreshIcon, {}), onClick: function () {
                                            inventoryPlanningService.refreshData(propertyId).then(setPlanningData);
                                        }, children: "Refresh" }), _jsx(Button, { variant: "contained", startIcon: _jsx(SaveIcon, {}), disabled: pendingChangesCount === 0 || saving, onClick: handleSaveBatch, color: pendingChangesCount > 0 ? "secondary" : "primary", sx: { mr: 1 }, children: saving ? 'Saving...' : "Save Changes ".concat(pendingChangesCount > 0 ? "(".concat(pendingChangesCount, ")") : '') }), _jsx(Button, { variant: "outlined", disabled: pendingChangesCount === 0 || saving, onClick: handleCancelChanges, color: "error", sx: { mr: 1 }, children: "Cancel Changes" }), _jsx(Button, { variant: rangeSelectionMode ? "contained" : "outlined", onClick: function () {
                                            setRangeSelectionMode(!rangeSelectionMode);
                                            setSelectedRange({}); // Reset any partial selection
                                        }, color: rangeSelectionMode ? "info" : "primary", children: rangeSelectionMode ? 'Exit Range Mode' : 'Range Select' })] }) })] }) }), _jsxs(Box, { display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, children: [_jsx(IconButton, { onClick: function () { return navigateWeek('prev'); }, children: _jsx(NavigateBefore, {}) }), _jsxs(Box, { textAlign: "center", children: [_jsxs(Typography, { variant: "h6", children: [format(currentWeekStart, 'MMM dd', { locale: es }), " - ", ' ', format(addDays(currentWeekStart, 13), 'MMM dd, yyyy', { locale: es })] }), rangeSelectionMode && (_jsx(Typography, { variant: "caption", color: "info.main", sx: { fontWeight: 'bold' }, children: selectedRange.start
                                    ? "Range Mode: Selected ".concat(selectedRange.start, " - Click another date to complete range")
                                    : 'Range Mode: Click a cell to start selecting date range' }))] }), _jsx(IconButton, { onClick: function () { return navigateWeek('next'); }, children: _jsx(NavigateNext, {}) })] }), _jsx(StyledTableContainer, { children: _jsxs(Table, { stickyHeader: true, size: "small", children: [_jsx(TableHead, { children: _jsxs(TableRow, { children: [_jsx(RoomTypeHeaderCell, { children: "Resource" }), dateColumns.map(function (date) { return (_jsx(TableCell, { align: "center", sx: {
                                            backgroundColor: '#1976d2',
                                            color: 'white',
                                            fontWeight: 'bold',
                                            fontSize: '0.75rem',
                                            minWidth: '60px'
                                        }, children: _jsxs(Box, { children: [_jsx(Typography, { variant: "caption", display: "block", children: format(new Date(date), 'EEE', { locale: es }) }), _jsx(Typography, { variant: "caption", display: "block", children: format(new Date(date), 'dd') }), _jsx(Typography, { variant: "caption", display: "block", children: format(new Date(date), 'MMM', { locale: es }) })] }) }, date)); })] }) }), _jsx(TableBody, { children: filteredData.interfaceData.roomTypes.map(function (roomType) { return (_jsxs(React.Fragment, { children: [_jsxs(TableRow, { children: [_jsx(RoomTypeHeaderCell, { children: _jsxs(Box, { children: [_jsxs(Typography, { variant: "subtitle2", fontWeight: "bold", children: ["\uD83C\uDFE0 ", roomType.name] }), _jsx(Typography, { variant: "caption", display: "block", color: "inherit", sx: { opacity: 0.8 }, children: "AVAILABILITY" })] }) }), dateColumns.map(function (date) {
                                                return renderCellContent(roomType.id, undefined, date);
                                            })] }, "room-availability-".concat(roomType.id)), filteredData.interfaceData.ratePlans
                                        .filter(function (ratePlan) { return ratePlan.room_type === roomType.id; })
                                        .map(function (ratePlan) { return (_jsxs(React.Fragment, { children: [_jsxs(TableRow, { children: [_jsx(RatePlanHeaderCell, { children: _jsxs(Box, { children: [_jsxs(Typography, { variant: "body2", fontWeight: "bold", children: ["\uD83D\uDCB0 ", ratePlan.name] }), _jsxs(Typography, { variant: "caption", display: "block", color: "inherit", sx: { opacity: 0.8 }, children: ["RATE (", ratePlan.rate_mode, ")"] })] }) }), dateColumns.map(function (date) {
                                                        return renderCellContent(ratePlan.room_type, ratePlan.id, date);
                                                    })] }, "rate-".concat(ratePlan.id)), visibleRestrictions.map(function (restriction) { return (_jsxs(TableRow, { children: [_jsx(RestrictionHeaderCell, { children: _jsxs(Box, { children: [_jsxs(Typography, { variant: "caption", display: "block", children: ["\uD83D\uDCCB ", restriction.label] }), _jsx(Typography, { variant: "caption", display: "block", sx: { opacity: 0.7 }, children: restriction.category.toUpperCase() })] }) }), dateColumns.map(function (date) {
                                                        return renderCellContent(undefined, ratePlan.id, date, restriction.value);
                                                    })] }, "restriction-".concat(ratePlan.id, "-").concat(restriction.value))); })] }, "rateplan-".concat(ratePlan.id))); })] }, "roomtype-".concat(roomType.id))); }) })] }) }), _jsxs(Dialog, { open: editDialog.open, onClose: function () { return setEditDialog({ open: false }); }, maxWidth: "sm", fullWidth: true, children: [_jsxs(DialogTitle, { children: ["Edit ", ((_b = editDialog.cellData) === null || _b === void 0 ? void 0 : _b.type) === 'availability' ? 'Availability' :
                                ((_c = editDialog.cellData) === null || _c === void 0 ? void 0 : _c.type) === 'rate' ? 'Rate' : 'Restriction'] }), _jsx(DialogContent, { children: _jsx(EditDialogContent, { cellData: editDialog.cellData, initialValue: editDialog.initialValue, initialDateRange: editDialog.dateRange, onSave: handleSaveCell, onCancel: function () { return setEditDialog({ open: false }); } }) })] }), _jsx(Snackbar, { open: !!error, autoHideDuration: 6000, onClose: function () { return setError(null); }, children: _jsx(Alert, { severity: "error", onClose: function () { return setError(null); }, children: error }) }), _jsx(Snackbar, { open: !!success, autoHideDuration: 3000, onClose: function () { return setSuccess(null); }, children: _jsx(Alert, { severity: "success", onClose: function () { return setSuccess(null); }, children: success }) })] }));
};
var EditDialogContent = function (_a) {
    var _b, _c;
    var cellData = _a.cellData, initialValue = _a.initialValue, initialDateRange = _a.initialDateRange, onSave = _a.onSave, onCancel = _a.onCancel;
    // Initialize value with proper default for boolean restrictions
    var initializeValue = function () {
        if ((cellData === null || cellData === void 0 ? void 0 : cellData.type) === 'restriction' &&
            (cellData === null || cellData === void 0 ? void 0 : cellData.restrictionType) &&
            getBooleanRestrictionTypes().includes(cellData.restrictionType)) {
            // For boolean restrictions, default to false if undefined/null
            return initialValue === true || initialValue === false ? initialValue : false;
        }
        return initialValue;
    };
    var _d = useState(initializeValue()), value = _d[0], setValue = _d[1];
    var _e = useState('USD'), currency = _e[0], setCurrency = _e[1]; // Default currency
    var _f = useState(initialDateRange || {
        from: (cellData === null || cellData === void 0 ? void 0 : cellData.date) || new Date().toISOString().split('T')[0],
        to: (cellData === null || cellData === void 0 ? void 0 : cellData.date) || new Date().toISOString().split('T')[0]
    }), dateRange = _f[0], setDateRange = _f[1];
    if (!cellData)
        return null;
    // Determine input type based on restriction type
    var isRestrictionBoolean = cellData.type === 'restriction' &&
        cellData.restrictionType &&
        getBooleanRestrictionTypes().includes(cellData.restrictionType);
    var isRestrictionNumeric = cellData.type === 'restriction' &&
        cellData.restrictionType &&
        getNumericRestrictionTypes().includes(cellData.restrictionType);
    var isBoolean = isRestrictionBoolean || (typeof initialValue === 'boolean' && cellData.type !== 'restriction');
    var isNumber = isRestrictionNumeric || (typeof initialValue === 'number' && cellData.type !== 'restriction');
    var isRate = cellData.type === 'rate';
    var isRangeEdit = dateRange.from !== dateRange.to;
    // Debug log to help troubleshoot
    console.log('CellEditDialog debug:', {
        cellType: cellData.type,
        restrictionType: cellData.restrictionType,
        initialValue: initialValue,
        currentValue: value,
        isBoolean: isBoolean,
        isNumber: isNumber,
        isRestrictionBoolean: isRestrictionBoolean,
        isRestrictionNumeric: isRestrictionNumeric
    });
    // Common currencies
    var currencies = [
        { code: 'USD', symbol: '$', name: 'US Dollar' },
        { code: 'EUR', symbol: '€', name: 'Euro' },
        { code: 'GBP', symbol: '£', name: 'British Pound' },
        { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
        { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
        { code: 'MXN', symbol: '$', name: 'Mexican Peso' },
        { code: 'COP', symbol: '$', name: 'Colombian Peso' }
    ];
    return (_jsxs(Box, { sx: { pt: 2 }, children: [_jsxs(Box, { sx: { mb: 3 }, children: [_jsx(Typography, { variant: "subtitle2", gutterBottom: true, children: "Apply to Date Range:" }), _jsxs(Box, { sx: { display: 'flex', gap: 2, alignItems: 'center' }, children: [_jsx(TextField, { label: "From Date", type: "date", value: dateRange.from, onChange: function (e) { return setDateRange(function (prev) { return (__assign(__assign({}, prev), { from: e.target.value })); }); }, InputLabelProps: {
                                    shrink: true,
                                }, size: "small" }), _jsx(TextField, { label: "To Date", type: "date", value: dateRange.to, onChange: function (e) { return setDateRange(function (prev) { return (__assign(__assign({}, prev), { to: e.target.value })); }); }, InputLabelProps: {
                                    shrink: true,
                                }, size: "small" })] }), isRangeEdit && (_jsxs(Typography, { variant: "caption", color: "info.main", sx: { mt: 1, display: 'block' }, children: ["This will apply the change to all dates from ", format(new Date(dateRange.from), 'PP'), " to ", format(new Date(dateRange.to), 'PP')] }))] }), _jsx(Divider, { sx: { my: 2 } }), _jsxs(Typography, { variant: "subtitle2", gutterBottom: true, children: [cellData.type === 'availability' ? 'Available Rooms' :
                        cellData.type === 'rate' ? 'Rate' : 'Restriction Value', ":"] }), isBoolean ? (_jsx(FormControlLabel, { control: _jsx(Switch, { checked: Boolean(value), onChange: function (e) { return setValue(e.target.checked); } }), label: "Active" })) : isRate ? (_jsxs(Box, { children: [_jsxs(Box, { sx: { display: 'flex', gap: 2, alignItems: 'flex-end' }, children: [_jsx(TextField, { type: "number", value: value || '', onChange: function (e) { return setValue(Number(e.target.value)); }, label: "Rate", sx: { flex: 1 }, InputProps: {
                                    startAdornment: ((_b = currencies.find(function (c) { return c.code === currency; })) === null || _b === void 0 ? void 0 : _b.symbol) || '$'
                                } }), _jsx(TextField, { select: true, value: currency, onChange: function (e) { return setCurrency(e.target.value); }, label: "Currency", sx: { minWidth: 120 }, children: currencies.map(function (curr) { return (_jsxs(MenuItem, { value: curr.code, children: [curr.code, " (", curr.symbol, ")"] }, curr.code)); }) })] }), _jsxs(Typography, { variant: "caption", color: "text.secondary", sx: { mt: 1, display: 'block' }, children: ["Selected: ", (_c = currencies.find(function (c) { return c.code === currency; })) === null || _c === void 0 ? void 0 : _c.name] })] })) : (_jsx(TextField, { fullWidth: true, type: isNumber ? 'number' : 'text', value: value || '', onChange: function (e) { return setValue(isNumber ? Number(e.target.value) : e.target.value); }, margin: "normal", label: cellData.type === 'availability' ? 'Available Rooms' : 'Value' })), _jsxs(Box, { sx: { mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }, children: [_jsx(Button, { onClick: onCancel, children: "Cancel" }), _jsxs(Button, { variant: "contained", onClick: function () { return onSave(value || initialValue || 0, dateRange, isRate ? currency : undefined); }, children: ["Save ", isRangeEdit ? 'to Date Range' : ''] })] })] }));
};
export default InventoryCalendarV2;
